import StaffAnnouncements from './StaffAnnouncements';
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { post, put } from '../utils/api';
import NotificationBell from './NotificationBell';

// ── Nav group definitions ────────────────────────────────────────────────────
// Each group: { id, icon, label, paths[] for auto-open detection }
// Items: { to, label, roles?, permission?, exact? }

const ROLE_LABELS = {
  super_admin:'Managing Director', admin:'System Administrator',
  sales_manager:'Customer Experience & Sales Manager',
  content_sales:'Brand & Content Officer',
  brand_content_officer:'Brand & Content Officer',
  production_team_lead:'Production Team Lead',
  production_assistant:'Production Assistant',
  production_staff:'Production Staff',
  inventory_officer:'Inventory & Materials Officer',
  inventory_auditor:'Inventory Auditor',
  procurement_logistics_officer:'Procurement & Logistics Officer',
  tailor:'Tailor', driver:'Driver', auditor:'Auditor',
  support_staff:'Facilities Assistant',
  customer_experience_officer:'Customer Experience Officer',
  field_sales_representative:'Field Sales Representative',
  front_desk_officer:'Front Desk Officer',
};

export default function AppShell() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPwModal, setShowPwModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [pwForm, setPwForm] = useState({ current:'', new:'', confirm:'' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const role = user?.role;
  const is = (...roles) => roles.includes(role);
  const can = (perm) => hasPermission(perm);

  const canViewJobOrders = can('job_orders:read') ||
    ['production_assistant','production_staff','tailor'].includes(role);

  // ── Group definitions ──────────────────────────────────────────────────────
  const groups = [
    {id:'my_records',icon:'👤',label:'My Profile',paths:['/my-profile'],items:[{to:'/my-profile',label:'View & Edit My Profile',show:true}]},
    {
      id:'front_desk',
      icon:'🏢',
      label:'Front Desk',
      paths:['/front-desk','/my-visitors'],
      items: [
        {
          to:'/front-desk',
          label:'Reception Workspace',
          show:can('front_desk:read')
        },
        {
          to:'/my-visitors',
          label:'My Visitors',
          show:can('visitors:host_official')
        },
      ]
    },
    {
      id:'dashboard', icon:'📊', label:'Dashboard',
      paths:['/','/emd','/manager-dashboard','/ai-briefing','/finance-dashboard','/hr-dashboard'],
      items: [
        { to:'/', label:'Overview', show: !is('inventory_officer','production_assistant','production_staff','tailor','driver','support_staff','customer_experience_officer','field_sales_representative') },
        { to:'/finance-dashboard', label:'Finance Dashboard', show: is('super_admin') },
        { to:'/production-dashboard', label:'Inventory Dashboard', show: is('inventory_officer') },
        { to:'/emd', label:'Executive Dashboard', show: is('super_admin') },
        { to:'/manager-dashboard', label:'Live Dashboard', show: is('super_admin','admin') },
        { to:'/ai-briefing', label:'✨ AI Briefing', show: is('super_admin') },
        { to:'/hr-dashboard', label:'👥 HR Dashboard', show: is('super_admin','hr_officer') },
      ]
    },
    {
      id:'brand_content', icon:'🎨', label:'Brand & Content',
      paths:['/bc-dashboard','/bc-content','/bc-campaigns','/bc-assets','/bc-blog','/bc-website','/bc-marketplace','/bc-resources'],
      items: [
        { to:'/bc-dashboard', label:'BC Dashboard', show: can('bc:read') },
        { to:'/bc-content', label:'Content', show: can('bc:read') },
        { to:'/bc-campaigns', label:'Campaigns', show: can('bc:read') },
        { to:'/bc-assets', label:'Digital Assets', show: can('bc:read') },
        { to:'/bc-blog', label:'Blog', show: can('bc:read') },
        { to:'/bc-website', label:'Website Listings', show: can('bc:read') },
        { to:'/bc-marketplace', label:'Marketplace', show: can('bc:read') },
        { to:'/bc-resources', label:'Resource Requests', show: can('bc:read') },
      ]
    },
    {
      id:'sales', icon:'🛒', label:'Sales & Customers',
      paths:['/customers','/orders','/invoices','/crm','/sales-manager-dashboard','/sales-targets'],
      items: [
        { to:'/customers', label:'Customers', show: can('customers:read') && !is('inventory_officer','production_team_lead','procurement_logistics_officer','support_staff') },
        { to:'/orders', label:'Orders', show: can('orders:read') && !is('inventory_officer','production_team_lead','procurement_logistics_officer','support_staff') },
        { to:'/invoices', label:'Invoices', show: can('invoices:read') && !is('inventory_officer','production_team_lead','procurement_logistics_officer','support_staff') },
        { to:'/crm', label: 'CRM', show: is('super_admin','admin','sales_manager','content_sales','brand_content_officer','customer_experience_officer','field_sales_representative') },
        { to:'/sales-manager-dashboard', label:'Sales Manager Dashboard', show: is('super_admin','sales_manager') },
        { to:'/sales-targets', label:'Sales & Commission', show: is('super_admin','sales_manager','content_sales','brand_content_officer','customer_experience_officer','field_sales_representative') },
      ]
    },
    {
      id:'procurement', icon:'📦', label:'Procurement',
      paths:['/suppliers','/supplier-dashboard','/purchase-requests','/purchase-orders','/requisitions','/bills','/expenses'],
      items: [
        { to:'/suppliers', label:'Suppliers', show: can('suppliers:read') },
        { to:'/supplier-dashboard', label:'Supplier Dashboard', show: can('suppliers:read') },
        { to:'/purchase-requests', label:'Purchase Requests', show: is('super_admin','sales_manager','production_team_lead','inventory_officer','procurement_logistics_officer','front_desk_officer','content_sales') },
        { to:'/purchase-orders', label:'Purchase Orders', show:
          can('purchase_orders:read') ||
          (
            can('orders:read')
          )
        },
        { to:'/requisitions', label:'Requisitions', show: is('super_admin','admin','sales_manager','production_team_lead','inventory_officer','procurement_logistics_officer','front_desk_officer','content_sales') },
        { to:'/bills', label:'Bills & Expenses', show: can('bills:read') },
        { to:'/expenses', label:'General Expenses', show: can('expenses:read') },
      ]
    },
    {
      id:'inventory', icon:'📚', label:'Inventory',
      paths:['/materials','/products','/custom-products','/inventory','/inventory-audit','/assets','/material-issues'],
      items: [
        { to:'/materials', label:'Raw Materials', show: can('materials:read') },
        { to:'/material-skus', label:'Material SKUs', show: is('super_admin','admin','inventory_officer') },
        { to:'/conversion-rules', label:'Conversion Rules', show: is('super_admin','admin','inventory_officer','production_team_lead') },
        { to:'/inventory-receipts', label:'Inventory Receipts', show: is('super_admin','admin','inventory_officer') },
        { to:'/material-issues', label:'Material Issues', show: is('super_admin','inventory_officer') },
        { to:'/material-conversion', label:'Conversion Requests', show: is('super_admin','admin','inventory_officer','production_team_lead') },
        { to:'/stock-counts', label:'Stock Counts', show: is('super_admin','admin','inventory_officer') },
        { to:'/inventory-adjustments', label:'Inventory Adjustments', show: is('super_admin','admin','inventory_officer') },
        { to:'/products', label:'Finished Goods', show: can('products:read') },
        { to:'/custom-products', label:'Custom Products', show: can('products:read') },
        { to:'/inventory', label:'Stock Ledger', show: can('inventory:read') },
        { to:'/inventory-audit', label:'Inventory Audit Workspace', show: is('inventory_auditor','inventory_officer','super_admin','admin') },
        { to:'/assets', label:'Assets & Equipment', show: is('super_admin','admin','front_desk_officer','inventory_officer') },
      ]
    },
    {
      id:'production', icon:'🏭', label:'Production',
      paths:['/job-orders','/production-counts','/job-costing-report'],
      items: [
        { to:'/job-orders', label:'Job Orders', show: canViewJobOrders },
        { to:'/production-counts', label:'Production Counts', show: is('super_admin','production_team_lead','inventory_officer','production_supervisor') },
        { to:'/job-costing-report', label:'Job Costing', show: is('super_admin','admin','inventory_officer') },
      ]
    },
    {
      id:'operations', icon:'🚚', label:'Operations',
      paths:['/deliveries','/operations-dashboard'],
      items: [
        { to:'/operations-dashboard', label:'📊 Operations Overview', show: can('deliveries:read') },
        { to:'/deliveries', label:'🚚 Deliveries', show: can('deliveries:read') },
      ]
    },
    {
      id:'finance', icon:'💰', label:'Finance',
      paths:['/payments','/banking','/cashbook','/payroll','/reports','/advanced-reports'],
      items: [
        { to:'/payments', label:'Payments', show: can('payments:read') },
        { to:'/banking', label:'Banking', show: is('super_admin') },
        { to:'/loans', label:'Loan Management', show: is('super_admin') },
        { to:'/cashbook', label:'Cashbook', show: can('cashbook:read') },
        { to:'/payroll', label:'Payroll', show: is('super_admin','admin','hr_officer') },
        { to:'/reports', label:'Reports', show: is('super_admin') },
        
      ]
    },
    {
      id:'people', icon:'👥', label:'Staff & HR',
      paths:['/non-production-assessments','/attendance','/leave','/departments','/roles-permissions','/users','/staff-biodata','/job-descriptions','/organogram','/onboarding','/offboarding','/staff-id-cards','/birthday-calendar','/my-payslips','/prospects','/payroll-enrolments','/documents'],
      items: [
        { to:'/attendance', label:'Attendance', show: is('super_admin','admin','hr_officer') || can('attendance:write') || can('overtime:request') },
        { to:'/leave', label:'Leave', show: can('leave:read') },
        { to:'/departments', label:'Departments', show: is('super_admin') },
        { to:'/roles-permissions', label:'Roles & Permissions', show: is('super_admin') },
        { to:'/users', label:'Users', show: can('users:read') },
        { to:'/staff-biodata', label:'Staff Profiles', show: is('super_admin','hr_officer') },
        { to:'/non-production-assessments', label:'Staff Assessments', show:true },
        { to:'/job-descriptions', label:'Job Descriptions', show: can('dashboard:read') },
        { to:'/organogram', label:'Organogram', show: can('dashboard:read') },
        { to:'/onboarding', label:'Onboarding', show: is('super_admin','hr_officer') },
        { to:'/prospects', label:'Prospects', show: is('super_admin','hr_officer') },
        { to:'/payroll-enrolments', label:'Payroll Enrolments', show: is('super_admin','hr_officer') },
        { to:'/documents', label:'Employment Documents', show: is('super_admin','hr_officer') },
          { to:'/document-templates', label:'Document Templates', show: is('super_admin') },
        { to:'/offboarding', label:'Offboarding', show: is('super_admin','hr_officer') },
        { to:'/staff-id-cards', label:'🪪 ID Cards', show: is('super_admin','admin') },
        { to:'/birthday-calendar', label:'🎂 Birthdays', show: can('birthday:read') },
        { to:'/my-payslips', label:'My Payslips', show: can('payslip:read') },
        { to:'/steward-contributions', label:'🏦 Loyalty Wallet', show: can('payslip:read') },
      ]
    },
    {
      id:'knowledge', icon:'📖', label:'Bloans Standard',
      paths:['/bloans-standard'],
      items: [
        { to:'/bloans-standard', label:'📖 Bloans Standard', show: can('dashboard:read') },
      ]
    },
    {
      id:'work', icon:'✅', label:'Work Management',
      paths:['/tasks','/staff-checklists','/team-reports'],
      items: [
        { to:'/tasks', label:'Tasks', show: can('tasks:read') },
        { to:'/staff-checklists', label:'Staff Checklists', show: is('super_admin','admin','production_team_lead','sales_manager','inventory_officer','front_desk_officer','content_sales','procurement_logistics_officer','brand_content_officer','hr_officer','expenses_clerk') },
        { to:'/team-reports', label:'Team Reports', show: !!user },
      ]
    },
    {
      id:'analytics', icon:'📈', label:'Analytics',
      paths:['/audit'],
      items: [
        { to:'/audit', label:'Audit Log', show: can('audit:read') },
      ]
    },
    {
      id:'admin', icon:'⚙', label:'Administration',
      paths:['/invoice-settings'],
      items: [
        { to:'/invoice-settings', label:'Invoice Settings', show: can('invoices:read') },
      ]
    },
  ];

  // ── Auto-open group containing current path ──────────────────────────────
  useEffect(() => {
    const currentPath = location.pathname;
    const initial = {};
    groups.forEach(g => {
      const match = g.paths.some(p =>
        p === '/' ? currentPath === '/' : currentPath.startsWith(p)
      ) || g.items.some(i => i.to === '/' ? currentPath === '/' : currentPath.startsWith(i.to));
      if (match) initial[g.id] = true;
    });
    // If nothing matched, open dashboard
    if (Object.keys(initial).length === 0) initial['dashboard'] = true;
    setOpenGroups(initial);
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const navItem = (to, label, show) => {
    if (!show) return null;
    return (
      <NavLink key={to} to={to} end={to==='/'}
        className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
        onClick={() => setSidebarOpen(false)}
        style={({ isActive }) => ({
          display:'block', padding:'7px 16px 7px 36px',
          fontSize:12, textDecoration:'none', borderRadius:6,
          color: isActive ? '#E8C466' : 'rgba(242,239,231,.7)',
          background: isActive ? 'rgba(196,154,60,.15)' : 'transparent',
          fontWeight: isActive ? 600 : 400,
          borderLeft: isActive ? '2px solid #C49A3C' : '2px solid transparent',
          marginBottom:1,
        })}>
        {label}
      </NavLink>
    );
  };

  const handleChangePw = async () => {
    if (pwForm.new !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    if (pwForm.new.length < 6) { setPwError('Password must be at least 6 characters'); return; }
    setPwSaving(true); setPwError('');
    try {
      await post('/auth/change-password', { currentPassword: pwForm.current, newPassword: pwForm.new });
      setShowPwModal(false);
      setPwForm({ current:'', new:'', confirm:'' });
      alert('Password changed successfully');
    } catch(err) { setPwError(err.message||'Failed to change password'); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="app">
      <div className={'sidebar-overlay'+(sidebarOpen?' open':'')} onClick={()=>setSidebarOpen(false)}/>

      <aside className={'sidebar'+(sidebarOpen?' open':'')}
        style={{display:'flex',flexDirection:'column',height:'100vh',overflowY:'auto'}}>

        {/* Logo */}
        <div className="sb-logo" style={{flexShrink:0}}>
          <h1>BloansBooks</h1>
          <span>Bloans Creations Ltd</span>
        </div>

        {/* Nav groups */}
        <nav className="sb-nav" style={{flex:1,overflowY:'auto',padding:'8px 8px'}}>
          {groups.map(group => {
            const visibleItems = group.items.filter(i => i.show);
            if (visibleItems.length === 0) return null;
            const isOpen = openGroups[group.id];

            return (
              <div key={group.id} style={{marginBottom:2}}>
                {/* Group header */}
                <div onClick={()=>toggleGroup(group.id)}
                  style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                    padding:'8px 10px',borderRadius:8,cursor:'pointer',
                    background:isOpen?'rgba(196,154,60,.08)':'transparent',
                    color: isOpen ? '#E8C466' : 'rgba(242,239,231,.85)',
                    userSelect:'none'}}>
                  <span style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:600}}>
                    <span style={{fontSize:14}}>{group.icon}</span>
                    {group.label}
                  </span>
                  <span style={{fontSize:10,color:'rgba(242,239,231,.4)',transition:'transform .2s',
                    transform:isOpen?'rotate(90deg)':'rotate(0deg)'}}>▶</span>
                </div>

                {/* Group items */}
                {isOpen && (
                  <div style={{marginTop:2,marginBottom:4}}>
                    {visibleItems.map(item => navItem(item.to, item.label, item.show))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div style={{flexShrink:0,padding:'12px 12px',borderTop:'1px solid var(--border-light)',
          background:'#fff'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
            <div style={{width:32,height:32,borderRadius:'50%',background:'#556B2F',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:13,fontWeight:700,color:'#F2EFE7',flexShrink:0}}>
              {user?.photo_url ? <img src={user.photo_url} alt={user.name} style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover'}} /> : user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:'#3B2A21',
                whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                {user?.name}
              </div>
              <div style={{fontSize:10,color:'#8A8A8A'}}>
                {ROLE_LABELS[user?.role]||user?.role}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:6}}>
            <button onClick={()=>setShowPwModal(true)}
              style={{flex:1,fontSize:11,padding:'5px',border:'1px solid #ddd',
                borderRadius:6,background:'#fff',cursor:'pointer',color:'#555'}}>
              Change Password
            </button>
            <button onClick={async()=>{await logout();navigate('/login');}}
              style={{flex:1,fontSize:11,padding:'5px',border:'1px solid #ddd',
                borderRadius:6,background:'#fff',cursor:'pointer',color:'#8F2E24'}}>
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main">
        <header className="topbar">
          <button
            type="button"
            className="menu-btn hamburger"
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            ☰
          </button>
          <div style={{flex:1}}/>
          <StaffAnnouncements />
          <NotificationBell />
        </header>
        <div className="content">
          <Outlet />
        </div>
      </main>

      {/* Change Password Modal */}
      {showPwModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-hdr">
              <span className="modal-title">Change Password</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPwModal(false)}>X</button>
            </div>
            <div className="modal-body">
              {pwError && <div className="alert alert-error mb-md">{pwError}</div>}
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input type="password" className="form-control" value={pwForm.current}
                  onChange={e=>setPwForm({...pwForm,current:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-control" value={pwForm.new}
                  onChange={e=>setPwForm({...pwForm,new:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input type="password" className="form-control" value={pwForm.confirm}
                  onChange={e=>setPwForm({...pwForm,confirm:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPwModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleChangePw} disabled={pwSaving}>
                {pwSaving?'Saving...':'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
